import React from 'react';
import { useTranslation } from 'react-i18next';

type ConflictFlowStep = 'dialogue' | 'locked' | 'external';

interface ConflictFlowIllustrationProps {
  currentStep: ConflictFlowStep;
  viewerSide?: 'person' | 'company';
}

const stepClass = (active: boolean) =>
  `h-100 rounded border p-3 ${active ? 'border-warning bg-warning bg-opacity-10' : 'bg-light'}`;

export default function ConflictFlowIllustration({
  currentStep,
  viewerSide
}: ConflictFlowIllustrationProps) {
  const { t } = useTranslation();

  return (
    <figure
      className="border rounded p-3 mb-3"
      aria-labelledby="conflict-flow-title"
      data-testid="conflict-flow-illustration"
    >
      <figcaption id="conflict-flow-title" className="fw-semibold mb-3">
        <i className="feather-map me-2 text-primary"></i>
        {t('conflictFlow.title')}
      </figcaption>
      <div className="row g-2 align-items-stretch">
        <div className="col-md-4">
          <div className={stepClass(currentStep === 'dialogue')}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge rounded-pill bg-primary">1</span>
              <i className="feather-message-circle text-primary"></i>
              <span className="fw-semibold">{t('conflictFlow.dialogueTitle')}</span>
            </div>
            <div className="small text-muted">{t('conflictFlow.dialogueBody')}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={stepClass(currentStep === 'locked')}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge rounded-pill bg-warning text-dark">2</span>
              <i className="feather-lock text-warning"></i>
              <span className="fw-semibold">{t('conflictFlow.lockedTitle')}</span>
            </div>
            <div className="small text-muted">{t('conflictFlow.lockedBody')}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={stepClass(currentStep === 'external')}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge rounded-pill bg-dark">3</span>
              <i className="feather-git-branch text-dark"></i>
              <span className="fw-semibold">{t('conflictFlow.outcomeTitle')}</span>
            </div>
            <div className="small border-start border-success border-3 ps-2 mb-2">
              <strong>{t('conflictFlow.agreementTitle')}</strong>{' '}
              {t('conflictFlow.agreementBody')}
            </div>
            <div className="small border-start border-danger border-3 ps-2">
              <strong>{t('conflictFlow.externalTitle')}</strong>{' '}
              {viewerSide === 'person'
                ? t('conflictFlow.externalBodyNoFee')
                : t('conflictFlow.externalBody')}
            </div>
          </div>
        </div>
      </div>
    </figure>
  );
}
